/**
 * Shared SVG marker for AutoGo vehicles on Google Maps.
 *
 * Renders a clean, top-down sedan inside a brand-colored pin with the licence
 * plate floating above. Designed to match AutoGo brand colors (#0a3042 navy +
 * #36e961 green) and to look nothing like Shrek :)
 */

const NAVY = '#0a3042';
const GREEN = '#36e961';
const WHITE = '#ffffff';

function escapeXml(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * Build the AutoGo vehicle marker SVG (string).
 * @param {Object} opts
 * @param {string} [opts.plate] License plate to show in the small label.
 * @param {number} [opts.heading] Heading in degrees (0 = north). Rotates the car body only.
 * @param {boolean} [opts.showPlate=true]
 */
export function buildVehicleMarkerSvg({ plate = '', heading = null, showPlate = true } = {}) {
  const safePlate = escapeXml((plate || '').toString().toUpperCase().slice(0, 10));
  const hasPlate = showPlate && safePlate.length > 0;
  const rot = Number.isFinite(Number(heading)) ? Number(heading) : 0;

  // Plate label sits at the top, pin underneath.
  // Total canvas: 64w x (hasPlate ? 86 : 64)h, anchor at bottom center of pin.
  const plateBlock = hasPlate
    ? `
        <g>
          <rect x="6" y="0" width="52" height="20" rx="5" fill="${NAVY}"/>
          <rect x="6" y="0" width="52" height="20" rx="5" fill="none" stroke="${GREEN}" stroke-width="1.5"/>
          <text x="32" y="14" text-anchor="middle"
                fill="${WHITE}"
                font-family="Manrope, system-ui, -apple-system, Segoe UI, Roboto, sans-serif"
                font-size="11" font-weight="700" letter-spacing="0.5">${safePlate}</text>
        </g>
        <line x1="32" y1="20" x2="32" y2="24" stroke="${NAVY}" stroke-width="2" stroke-linecap="round"/>
      `
    : '';

  const yOffset = hasPlate ? 24 : 0;
  const totalH = hasPlate ? 86 : 64;

  // Pin centered at (32, yOffset+30) with bottom tip at (32, yOffset+62)
  const pinPath = `M 32 ${yOffset} C 18 ${yOffset}, 8 ${yOffset + 10}, 8 ${yOffset + 28} C 8 ${yOffset + 44}, 22 ${yOffset + 56}, 32 ${yOffset + 62} C 42 ${yOffset + 56}, 56 ${yOffset + 44}, 56 ${yOffset + 28} C 56 ${yOffset + 10}, 46 ${yOffset}, 32 ${yOffset} Z`;

  return `
<svg xmlns="http://www.w3.org/2000/svg" width="64" height="${totalH}" viewBox="0 0 64 ${totalH}">
  <defs>
    <filter id="agShadow" x="-20%" y="-20%" width="140%" height="140%">
      <feGaussianBlur in="SourceAlpha" stdDeviation="1.2"/>
      <feOffset dx="0" dy="1.5" result="off"/>
      <feComponentTransfer><feFuncA type="linear" slope="0.35"/></feComponentTransfer>
      <feMerge><feMergeNode/><feMergeNode in="SourceGraphic"/></feMerge>
    </filter>
  </defs>
  ${plateBlock}
  <g filter="url(#agShadow)">
    <path d="${pinPath}" fill="${NAVY}"/>
    <path d="${pinPath}" fill="none" stroke="${GREEN}" stroke-width="1.75"/>
  </g>
  <g transform="translate(32 ${yOffset + 30}) rotate(${rot})">
    <!-- Top-down sedan: smooth body, windshields and headlights -->
    <path d="M -8.5,-13 C -8.5,-15.5 -6.5,-16.5 -3.5,-16.5 L 3.5,-16.5 C 6.5,-16.5 8.5,-15.5 8.5,-13 L 8.5,12.5 C 8.5,15 6.5,16.5 3.5,16.5 L -3.5,16.5 C -6.5,16.5 -8.5,15 -8.5,12.5 Z"
          fill="${GREEN}"/>
    <!-- Front windshield (faces heading direction) -->
    <path d="M -6,-12 L 6,-12 L 7,-5 L -7,-5 Z" fill="${NAVY}" opacity="0.92"/>
    <!-- Rear windshield -->
    <path d="M -7,5 L 7,5 L 6,11 L -6,11 Z" fill="${NAVY}" opacity="0.6"/>
    <!-- Headlights -->
    <rect x="-6.5" y="-15.5" width="3" height="1.6" rx="0.6" fill="${WHITE}"/>
    <rect x="3.5"  y="-15.5" width="3" height="1.6" rx="0.6" fill="${WHITE}"/>
    <!-- Tail lights -->
    <rect x="-6.5" y="14" width="3" height="1.4" rx="0.5" fill="#ff5252" opacity="0.85"/>
    <rect x="3.5"  y="14" width="3" height="1.4" rx="0.5" fill="#ff5252" opacity="0.85"/>
  </g>
</svg>`.trim();
}

/**
 * Build the Google Maps `icon` config for `new google.maps.Marker`.
 * Returns null if Google Maps SDK isn't loaded yet.
 */
export function buildVehicleMarkerIcon(opts = {}) {
  if (typeof window === 'undefined' || !window.google?.maps) return null;
  const svg = buildVehicleMarkerSvg(opts);
  const hasPlate = opts.showPlate !== false && Boolean(opts.plate);
  const totalH = hasPlate ? 86 : 64;
  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
    scaledSize: new window.google.maps.Size(64, totalH),
    // Pin tip sits at the bottom-center of the SVG → use that as the geographic anchor
    anchor: new window.google.maps.Point(32, totalH),
  };
}

export const AUTOGO_BRAND_COLORS = { NAVY, GREEN, WHITE };
